import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getExistPlanByID, addPlan, updatePlan } from "../../services/api/SuperAdminApi";
import './SAPlanForm.scss';
import { useLoading } from "../../context/LoadingContext";

export const SAPlanForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showLoading, hideLoading } = useLoading();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        price: '',
        duration: '',
        caseLimit: '',
        userLimit: '',
        type: 'prm',
        rights: {
            cases: false,
            clients: false,
            expenses: false,
            invoices: false,
            labels: false,
            mobileAppAccess: false,
            notes: false,
            tasks: false,
            teams: false,
            branch: false
        }
    });

    useEffect(() => {
        if (id) {
            const fetchPlan = async () => {
                showLoading('Loading plan details...');
                try {
                    const response = await getExistPlanByID(id);
                    setFormData(response.data);
                } catch (error) {
                    console.error("Error fetching plan:", error);
                } finally {
                    hideLoading();
                }
            };
            fetchPlan();
        }
    }, [id]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name.startsWith('rights.')) {
            const rightName = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                rights: {
                    ...prev.rights,
                    [rightName]: checked
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        
        setIsSubmitting(true);
        showLoading('Saving plan...');
        
        const apiData = {
            name: formData.name,
            price: parseInt(formData.price),
            duration: parseInt(formData.duration),
            caseLimit: parseInt(formData.caseLimit),
            userLimit: parseInt(formData.userLimit),
            type: formData.type,
            rights: formData.rights
        };

        try {
            if (id) {
                await updatePlan(id, apiData);
            } else {
                await addPlan(apiData);
            }
            navigate('/home/superadmin');
        } catch (error) {
            console.error("Error saving plan:", error);
        } finally {
            setIsSubmitting(false);
            hideLoading();
        }
    };

   return (
        <div className="saplan-form-container">
            <div className="saplan-form-wrapper">
                <div className="saplan-form-header">
                    <h1 className="saplan-form-title">{id ? 'Edit Plan' : 'Add New Plan'}</h1>
                </div>
                
                <hr className="saplan-form-divider" />
                
                <form onSubmit={handleSubmit} className="form-content">
                {/* Basic Plan Information Section */}
                <div className="form-section">
                    <h3>Basic Information</h3>
                    
                    <div className="form-row">
                        <div className="form-group">
                            <label>Plan Name <span className="saplan-form-required">*</span></label>
                            <input 
                                type="text" 
                                name="name" 
                                value={formData.name} 
                                onChange={handleChange}
                                placeholder="Enter plan name"
                                required 
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Type <span className="saplan-form-required">*</span></label>
                            <select 
                                name="type" 
                                value={formData.type} 
                                onChange={handleChange}
                                required 
                            >
                                <option value="std">Standard</option>
                                <option value="prm">Premium</option>
                                <option value="ent">Enterprise</option>
                                <option value="ind">Individual</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Pricing & Limits Section */}
                <div className="form-section">
                    <h3>Pricing & Limits</h3>
                    
                    <div className="form-row">
                        <div className="form-group">
                            <label>Price (₹) <span className="saplan-form-required">*</span></label>
                            <input 
                                type="number" 
                                name="price" 
                                value={formData.price} 
                                onChange={handleChange}
                                onWheel={(e) => e.target.blur()}
                                placeholder="Enter price"
                                min="0"
                                required 
                                className="no-spinner"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Duration (days) <span className="saplan-form-required">*</span></label>
                            <input 
                                type="number" 
                                name="duration" 
                                value={formData.duration} 
                                onChange={handleChange}
                                onWheel={(e) => e.target.blur()}
                                placeholder="Enter duration in days"
                                min="1"
                                required 
                                className="no-spinner"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Case Limit <span className="saplan-form-required">*</span></label>
                            <input 
                                type="number" 
                                name="caseLimit" 
                                value={formData.caseLimit} 
                                onChange={handleChange}
                                onWheel={(e) => e.target.blur()}
                                placeholder="Enter maximum cases allowed"
                                min="1"
                                required 
                                className="no-spinner"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>User Limit <span className="saplan-form-required">*</span></label>
                            <input 
                                type="number" 
                                name="userLimit" 
                                value={formData.userLimit} 
                                onChange={handleChange}
                                onWheel={(e) => e.target.blur()}
                                placeholder="Enter maximum users allowed"
                                min="1"
                                required 
                                className="no-spinner"
                            />
                        </div>
                    </div>
                </div>
                
                {/* Module Rights Section */}
                <div className="form-section">
                    <h3>Module Rights</h3>
                    <div className="rights-checkboxes">
                        {Object.keys(formData.rights).map(right => (
                            <div key={right} className="checkbox-group">
                                <label htmlFor={`right-${right}`}>
                                    {right.charAt(0).toUpperCase() + right.slice(1).replace(/([A-Z])/g, ' $1')}
                                </label>
                                <input
                                    type="checkbox"
                                    id={`right-${right}`}
                                    name={`rights.${right}`}
                                    checked={formData.rights[right]}
                                    onChange={handleChange}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                
                <button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : (id ? 'Update Plan' : 'Save Plan')}
                </button>
            </form>
        </div>
        </div>
    );
};